import { Button, Divider, DownloadIcon, ExcelIcon, type TypeSelectOption } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { getReceiveStatusColumnDefs } from '~/utils';

import { RECEIVE_PAGE_SIZE } from '~/constants';

import type { TypePagination } from '~/types/common.types';
import type { TypeReceiveStatusRow } from '~/types/receive.types';

import styles from './ReceiveStatusTable.module.scss';

type TypeReceiveStatusTableProps<T extends TypeReceiveStatusRow> = {
  mode?: 'DEFAULT' | '3PL';
  data: T[] | null;
  pagination: TypePagination;
  isDownloadReady: boolean;
  selectedItems: T[];
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: IRowNode[]) => void;
  onClickViewDetail: (data: T) => void;
  onClickReceiveStatusExcelDownload: () => void;
  onClickSpecificationDownload: () => void;
};

const ReceiveStatusTable = <T extends TypeReceiveStatusRow>({
  mode = 'DEFAULT',
  data,
  pagination,
  isDownloadReady,
  selectedItems,
  onChangePageLimit,
  onChangePage,
  onChangeRowSelection,
  onClickViewDetail,
  onClickReceiveStatusExcelDownload,
  onClickSpecificationDownload,
}: TypeReceiveStatusTableProps<T>) => {
  const { columnDefs } = getReceiveStatusColumnDefs({
    mode,
    onClickViewDetail: (row) => onClickViewDetail(row as T),
  });

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              leftIcon={<DownloadIcon size={16} />}
              disabled={selectedItems.length === 0}
              onClick={onClickSpecificationDownload}
            >
              서명확인용 거래명세서
            </Button>
            <Divider className={styles['divider']} orientation="vertical" color="gray100" />
            <Button
              variant="outlined"
              color="tertiary"
              size="tiny"
              disabled={!isDownloadReady}
              leftIcon={<ExcelIcon size={16} />}
              onClick={onClickReceiveStatusExcelDownload}
            >
              엑셀 전체 다운로드
            </Button>
          </div>
        }
      />
      <Table
        className={styles['table']}
        data={data}
        options={{ columnDefs }}
        rowStatusType={data === null ? 'INIT' : 'EMPTY'}
        onSelectionChange={onChangeRowSelection}
      />
      <TableBottomField
        pageSizeOptions={Object.values(RECEIVE_PAGE_SIZE)}
        currentPage={pagination.currentPage}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={onChangePage}
      />
    </div>
  );
};

export default ReceiveStatusTable;
